import {Country} from '@hconnect/apiclient'
import {flagPathByCountryCode} from '@hconnect/uikit'
import {TextField} from '@material-ui/core'
import React from 'react'
import {Controller, FieldValues} from 'react-hook-form'

import {useTranslation} from '../../../../hooks/useTranslation'
import {CountriesDropdown} from '../../../CountriesDropdown/deprecated/CountriesDropdown'
import {UserInformationInputSkeleton} from '../../Skeleton/UserInformationInputSkeleton'
import {UserInformationProps} from '../types'
import {useStyles} from '../UserInput.styles'

type UserCountryInputProps<T extends FieldValues> = UserInformationProps<T> & {
  countriesIds: string[]
  selectedCountry?: Country
}

export const UserCountryInput = <T extends FieldValues>({
  readonlyMode,
  formMethods,
  countriesIds,
  isLoading,
  selectedCountry,
  name
}: UserCountryInputProps<T>) => {
  const {t} = useTranslation()
  const errors = formMethods.formState.errors
  const touched = formMethods.formState.touchedFields
  const classes = useStyles()
  if (isLoading) {
    return <UserInformationInputSkeleton />
  }

  const countriesData = window.intlTelInputGlobals.getCountryData()

  const countryDataFilter = (countryId: string) =>
    countryId === 'MOCK'
      ? {name: 'MOCK', iso2: 'MOCK', dialCode: 'MOCK'}
      : countriesData.find((countryData) => countryData.iso2.toUpperCase() === countryId)

  const formattedCountryData = countriesIds
    .filter((countryId) => countryDataFilter(countryId))
    .map((countryId) => {
      return {
        label: countryDataFilter(countryId)?.name ?? '',
        value: countryDataFilter(countryId)?.iso2.toUpperCase() ?? ''
      }
    })
    .sort((current, next) => {
      if (current.label < next.label) {
        return -1
      }
      if (current.label > next.label) {
        return 1
      }
      return 0
    })

  return (
    <>
      {readonlyMode && selectedCountry ? (
        <TextField
          label={t('userInformationForm.country')}
          value={
            formattedCountryData.find(
              (countryData) => countryData.value === selectedCountry?.countryCode.toUpperCase()
            )?.label || selectedCountry?.countryName
          }
          InputProps={{
            readOnly: true,
            disableUnderline: true,
            ...(selectedCountry?.countryCode !== 'MOCK' && {
              startAdornment: (
                <img
                  src={flagPathByCountryCode(selectedCountry?.countryCode)}
                  alt={`${selectedCountry?.countryCode}-flag`}
                  style={{marginRight: '12px'}}
                />
              )
            })
          }}
          InputLabelProps={{shrink: true}}
          margin="normal"
          fullWidth
        />
      ) : (
        countriesIds.length > 1 && (
          <Controller
            name={name}
            control={formMethods.control}
            render={({field}) => (
              <CountriesDropdown
                {...field}
                value={field.value || '-'}
                data-test-id={`userInformationForm-${field.name}`}
                select={!readonlyMode}
                className={classes.textField}
                style={{width: '450px'}}
                label={t('userInformationForm.country')}
                onChange={(e) => {
                  formMethods.setValue('country' as any, e.target.value as any, {
                    shouldValidate: true
                  })
                  formMethods.setValue('defaultBranding' as any, '' as any, {
                    shouldValidate: true
                  })
                  formMethods.setValue('primaryOrganisationalUnit' as any, '' as any, {
                    shouldValidate: true
                  })
                  formMethods.setValue('marketId' as any, '' as any, {
                    shouldValidate: true
                  })
                  formMethods.setValue('defaultLocale' as any, '' as any, {
                    shouldValidate: true
                  })
                }}
                SelectProps={{
                  MenuProps: {
                    style: {
                      height: '400px'
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left'
                    },
                    getContentAnchorEl: null
                  }
                }}
                margin="normal"
                error={touched['country'] && !!errors.country}
                helperText={errors?.[name]?.message as string}
                InputProps={{
                  readOnly: readonlyMode,
                  disableUnderline: readonlyMode
                }}
                countriesList={formattedCountryData}
                defaultSelectTitle={t('userInformationForm.select')}
              />
            )}
          />
        )
      )}
    </>
  )
}
